import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { API_URL_SERVER } from '../api/api';

export const AuthContext = createContext();

// Create Axios instance
const api = axios.create({
    baseURL: API_URL_SERVER,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Axios interceptor to add token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Axios interceptor to handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            const message = error.response.data.message || 'Unauthorized';
            if (message === 'Invalid token: User logged in from another session') {
                toast.error('Your session was logged out because you logged in from another device.');
                localStorage.removeItem('token');
                window.location.href = '/login';
            } else if (message === 'Token expired') {
                toast.error('Your session has expired. Please log in again.');
                localStorage.removeItem('token');
                window.location.href = '/login';
            } else {
                toast.error('Authentication error. Please log in again.');
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const response = await api.get('/users/me');
                    setUser({
                        username: response.data.username,
                        role: response.data.role,
                        trades: response.data.trades,
                        accountIds: response.data.accountIds || [],
                        fullAccessAccountIds: response.data.fullAccessAccountIds || [],
                        can_start_stop: response.data.can_start_stop || false,
                        show_lot_size: response.data.show_lot_size || false,
                        can_edit_lot: response.data.can_edit_lot || false,
                        manual_trade: response.data.manual_trade || false,
                    });
                } catch (error) {
                    console.error('Error fetching user:', error.response?.data || error.message);
                    localStorage.removeItem('token');
                    setUser(null);
                }
            }
            setLoading(false);
        };
        checkAuth();
    }, []);

    const login = async (username, password) => {
        try {
            // Direct login attempt
            const response = await api.post('/login', { username, password });
            console.log('Login API response:', response.data);

            if (response.data.status === 'success') {
                localStorage.setItem('token', response.data.token);
                setUser({
                    username: response.data.user.username,
                    role: response.data.user.role,
                    trades: response.data.user.trades,
                    accountIds: response.data.user.accountIds || [],
                    fullAccessAccountIds: response.data.user.fullAccessAccountIds || [],
                    can_start_stop: response.data.user.can_start_stop || false,
                    show_lot_size: response.data.user.show_lot_size || false,
                    can_edit_lot: response.data.user.can_edit_lot || false,
                    manual_trade: response.data.user.manual_trade || false,
                });
                toast.success('Logged in successfully!');
                return { success: true };
            }
            // Handle any other status as failure
            return { success: false, message: response.data.message || 'Login failed' };
        } catch (error) {
            console.error('Login error:', error.response?.data || error.message);
            const message = error.response?.data?.message || 'Login failed';
            return { success: false, message };
        }
    };

    const logout = async () => {
        try {
            await api.post('/logout');
            toast.success('Logged out successfully!');
        } catch (error) {
            console.error('Error during logout:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || 'Logout failed');
        }
        localStorage.removeItem('token');
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};